import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { RefreshCw, Key, Copy, Trash2, CheckCircle, XCircle, Clock, Download } from 'lucide-react';

const fmt = (d) => {
  if (!d) return '-';
  try {
    const dt = new Date(d);
    return dt.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return d; }
};

export default function SyncAccessPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newKey, setNewKey] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/sync/status');
      setStatus(res.data);
    } catch {
      toast.error('Error al cargar estado de sync');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const generateKey = async () => {
    setGeneratingKey(true);
    try {
      const res = await api.post('/sync/generate-key');
      setNewKey(res.data.api_key);
      toast.success('Clave API generada');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar clave');
    } finally {
      setGeneratingKey(false);
    }
  };

  const revokeKey = async () => {
    try {
      await api.delete('/sync/key');
      setNewKey('');
      toast.success('Clave revocada');
      loadStatus();
    } catch {
      toast.error('Error al revocar clave');
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newKey);
    toast.success('Clave copiada al portapapeles');
  };

  const downloadScript = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sync/download-script`, '_blank');
  };

  if (loading) return <div className="p-6 text-muted-foreground">Cargando...</div>;

  const last = status?.last_sync;
  const tableStats = status?.table_stats || {};

  return (
    <div className="space-y-6" data-testid="sync-access-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Sincronizacion Access</h2>
          <p className="text-sm text-muted-foreground">
            Sincroniza automaticamente los datos de Access cada 15 minutos
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadStatus} data-testid="sync-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ultima Sincronizacion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {last ? (
                <>
                  {last.status === 'ok' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <div>
                    <p className="text-sm font-semibold">{fmt(last.timestamp)}</p>
                    <p className="text-xs text-muted-foreground">
                      {last.tables_synced} tablas | {last.total_imported + last.total_updated} registros
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Sin sincronizaciones</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">API Key</CardTitle>
          </CardHeader>
          <CardContent>
            {status?.api_key_configured ? (
              <div>
                <Badge variant="outline" className="text-green-600 border-green-300">Configurada</Badge>
                {status.api_key_last_used && (
                  <p className="text-xs text-muted-foreground mt-1">Ultimo uso: {fmt(status.api_key_last_used)}</p>
                )}
              </div>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-300">No configurada</Badge>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Sincronizaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{status?.history?.length || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* API Key Management */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2"><Key className="w-4 h-4" /> Clave API del Agente</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Genera una clave API para autenticar el script de sincronizacion. Esta clave se configura en el archivo <code>config.json</code> del agente Windows.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={generateKey} disabled={generatingKey} data-testid="generate-key-btn">
              <Key className="w-3.5 h-3.5 mr-1" />
              {status?.api_key_configured ? 'Regenerar Clave' : 'Generar Clave'}
            </Button>
            {status?.api_key_configured && (
              <Button size="sm" variant="destructive" onClick={revokeKey} data-testid="revoke-key-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Revocar
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={downloadScript} data-testid="download-script-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Descargar Script
            </Button>
          </div>
          {newKey && (
            <div className="bg-muted p-3 rounded-md" data-testid="api-key-display">
              <p className="text-xs text-muted-foreground mb-1">Copia esta clave (no se mostrara de nuevo):</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-background px-2 py-1 rounded flex-1 break-all">{newKey}</code>
                <Button size="sm" variant="ghost" onClick={copyKey}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table Stats */}
      {Object.keys(tableStats).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Estado por Tabla</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-2">Tabla</th>
                    <th className="text-right py-2 px-2">Recibidos</th>
                    <th className="text-right py-2 px-2">Importados</th>
                    <th className="text-right py-2 px-2">Actualizados</th>
                    <th className="text-right py-2 px-2">Errores</th>
                    <th className="text-right py-2 px-2">Ultima Sync</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(tableStats).map((s) => (
                    <tr key={s.table_name} className="border-b hover:bg-muted/30">
                      <td className="py-1.5 px-2 font-medium">{s.table_name}</td>
                      <td className="py-1.5 px-2 text-right">{s.rows_received?.toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right text-green-600">{s.rows_imported?.toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right text-blue-600">{s.rows_updated?.toLocaleString()}</td>
                      <td className="py-1.5 px-2 text-right text-red-600">{s.errors || 0}</td>
                      <td className="py-1.5 px-2 text-right text-muted-foreground">{fmt(s.last_sync)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {status?.history?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Historial de Sincronizaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {status.history.map((h, i) => (
                <div key={i} className="flex items-center justify-between text-xs border-b pb-2">
                  <div className="flex items-center gap-2">
                    {h.status === 'ok' ? (
                      <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-red-500" />
                    )}
                    <span>{fmt(h.timestamp)}</span>
                  </div>
                  <div className="flex gap-3 text-muted-foreground">
                    <span>{h.tables_synced} tablas</span>
                    <span className="text-green-600">+{h.total_imported}</span>
                    <span className="text-blue-600">~{h.total_updated}</span>
                    {h.total_errors > 0 && <span className="text-red-600">!{h.total_errors}</span>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Instrucciones de Configuracion</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground space-y-2">
          <ol className="list-decimal list-inside space-y-1.5">
            <li>Haz click en <strong>"Generar Clave"</strong> y copia la API key</li>
            <li>Descarga el script con <strong>"Descargar Script"</strong></li>
            <li>Copia los archivos a un PC de la red que tenga acceso al .mdb</li>
            <li>Edita <code>config.json</code> con la ruta del .mdb y la API key</li>
            <li>Instala Python 3.8+ y <code>pyodbc</code> en ese PC</li>
            <li>Configura una <strong>Tarea Programada de Windows</strong> que ejecute el script cada 15 minutos</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
